import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import {
  InvestmentsAPI,
  ImportSource,
  ImportPreviewResult,
  ImportPreviewRow,
  ImportCommitResult,
} from '../services/investments.ts';
import { formatCurrency } from '../utils/currency.ts';
import StatusChip from '../components/ui/StatusChip.tsx';
import { investmentCategoryLabel } from '../utils/labels.ts';

type Step = 1 | 2 | 3 | 4;

const SOURCES: {
  id: ImportSource;
  title: string;
  help: string;
  formats: string;
  accept: string;
}[] = [
  {
    id: 'b3_posicao',
    title: 'Área do Investidor B3 — Excel Posição',
    help: 'Em investidor.b3.com.br → Extratos e Informes → Posição → Exportar Excel.',
    formats: '.xlsx',
    accept:
      '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel',
  },
  {
    id: 'b3_movimentacao',
    title: 'Área do Investidor B3 — Excel Movimentação',
    help: 'Mesmo portal → Movimentação → período desejado → Exportar Excel.',
    formats: '.xlsx',
    accept:
      '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel',
  },
  {
    id: 'xp',
    title: 'XP Investimentos (arquivo da corretora)',
    help: 'No app/site XP → Relatórios → Posição / Extrato → baixar CSV ou Excel.',
    formats: '.csv · .xlsx',
    accept:
      '.csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  {
    id: 'clear',
    title: 'Clear (arquivo da corretora)',
    help: 'Clear → Relatórios → Posição consolidada → exportar CSV ou Excel.',
    formats: '.csv · .xlsx',
    accept:
      '.csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  {
    id: 'btg',
    title: 'BTG Pactual (arquivo da corretora)',
    help: 'BTG Banking → Investimentos → Extratos → exportar posição CSV ou Excel.',
    formats: '.csv · .xlsx',
    accept:
      '.csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
];

function sourceMeta(id: ImportSource) {
  return SOURCES.find(s => s.id === id) || SOURCES[0];
}

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function actionTone(action: string): 'green' | 'amber' | 'slate' {
  if (action === 'novo') return 'green';
  if (action === 'atualizar') return 'amber';
  return 'slate';
}

function actionLabel(action: string) {
  if (action === 'novo') return 'Novo';
  if (action === 'atualizar') return 'Atualizar valor';
  return 'Ignorar';
}

function categoryShort(cat: string) {
  try {
    return investmentCategoryLabel(cat);
  } catch {
    return cat;
  }
}

const ImportInvestments: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>(1);
  const [source, setSource] = useState<ImportSource>('b3_posicao');
  const [file, setFile] = useState<File | null>(null);
  const [parseError, setParseError] = useState<string>('');
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [rows, setRows] = useState<ImportPreviewRow[]>([]);
  const [confirmUpsert, setConfirmUpsert] = useState(false);
  const [commitResult, setCommitResult] = useState<ImportCommitResult | null>(null);
  const [commitError, setCommitError] = useState('');

  const meta = sourceMeta(source);

  const summary = useMemo(() => {
    let novos = 0;
    let atualizar = 0;
    let ignorados = 0;
    let valorTotal = 0;
    for (const r of rows) {
      const effective = !r.include || r.action === 'ignorar' ? 'ignorar' : r.action;
      if (effective === 'novo') {
        novos++;
        valorTotal += r.valor;
      } else if (effective === 'atualizar') {
        atualizar++;
        valorTotal += r.valor;
      } else {
        ignorados++;
      }
    }
    return {
      novos,
      atualizar,
      ignorados,
      valorTotal,
      historyPoints: novos + atualizar,
    };
  }, [rows]);

  const updateNames = useMemo(
    () =>
      rows
        .filter(r => r.include && r.action === 'atualizar')
        .map(r => r.ticker)
        .join(', '),
    [rows]
  );

  const previewMutation = useMutation(async (f: File) => InvestmentsAPI.importPreview(source, f), {
    onSuccess: data => {
      setParseError('');
      setPreview(data);
      setRows(data.rows.map(r => ({ ...r, include: r.action !== 'ignorar' })));
      setConfirmUpsert(false);
      setStep(3);
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        `O arquivo enviado não parece ser um ${meta.title}. Verifique se exportou o relatório correto e tente novamente.`;
      setParseError(msg);
      setPreview(null);
      setRows([]);
    },
  });

  const commitMutation = useMutation(
    async () => {
      if (!preview) throw new Error('Preview ausente');
      const payloadRows = rows.map(r => ({
        ...r,
        action: !r.include ? ('ignorar' as const) : r.action,
        include: r.include && r.action !== 'ignorar',
      }));
      return InvestmentsAPI.importCommit({
        source,
        filename: preview.filename,
        rows: payloadRows,
        confirmUpsert: summary.atualizar > 0 ? confirmUpsert : true,
      });
    },
    {
      onSuccess: data => {
        setCommitResult(data);
        setCommitError('');
        queryClient.invalidateQueries(['investments']);
        setStep(4);
      },
      onError: (err: any) => {
        setCommitError(
          err?.response?.data?.error || err?.message || 'Erro ao confirmar importação'
        );
      },
    }
  );

  const onPickFile = (f: File | null) => {
    if (!f) return;
    setFile(f);
    setParseError('');
    setPreview(null);
  };

  const resetToSource = () => {
    setStep(1);
    setFile(null);
    setParseError('');
    setPreview(null);
    setRows([]);
    setConfirmUpsert(false);
    setCommitResult(null);
    setCommitError('');
  };

  const toggleIgnore = (rowKey: string) => {
    setRows(prev =>
      prev.map(r => {
        if (r.rowKey !== rowKey) return r;
        if (r.action === 'ignorar') {
          // restore based on match
          return {
            ...r,
            action: r.matchedInvestmentId ? 'atualizar' : 'novo',
            include: true,
          };
        }
        return { ...r, action: 'ignorar', include: false };
      })
    );
  };

  const canConfirm = summary.atualizar === 0 || confirmUpsert;

  const stepClass = (n: Step) => {
    if (step > n) return 'import-step done';
    if (step === n) return 'import-step active';
    return 'import-step';
  };
  const stepNum = (n: Step) => (step > n ? '✓' : String(n));

  const layoutClass = step === 3 ? 'form-page import-layout-wide' : 'form-page import-layout';

  const subtitle =
    step === 1
      ? 'Escolha a fonte · passo 1 de 4'
      : step === 2
        ? 'Upload · passo 2 de 4'
        : step === 3
          ? 'Revisão · passo 3 de 4'
          : 'Confirmação · passo 4 de 4';

  return (
    <div className={layoutClass}>
      <div className="breadcrumb" style={{ marginBottom: 4 }}>
        Patrimônio / Investimentos / Importar
      </div>
      <h1 className="page-title" style={{ marginBottom: 4 }}>
        Importar investimentos
      </h1>
      <div className="page-subtitle" style={{ marginBottom: 16 }}>
        {subtitle}
      </div>

      {step === 1 && (
        <Link to="/investments" className="back-link">
          ← Voltar aos investimentos
        </Link>
      )}
      {step === 2 && (
        <button
          type="button"
          className="back-link"
          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
          onClick={() => setStep(1)}
        >
          ← Voltar à escolha de fonte
        </button>
      )}
      {step === 3 && (
        <button
          type="button"
          className="back-link"
          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
          onClick={() => setStep(2)}
        >
          ← Voltar ao upload
        </button>
      )}
      {step === 4 && (
        <Link to="/investments" className="back-link">
          ← Voltar aos investimentos
        </Link>
      )}

      <div className="import-steps">
        <div className={stepClass(1)}>
          <span className="import-step-num">{stepNum(1)}</span> Fonte
        </div>
        <div className={stepClass(2)}>
          <span className="import-step-num">{stepNum(2)}</span> Upload
        </div>
        <div className={stepClass(3)}>
          <span className="import-step-num">{stepNum(3)}</span> Revisão
        </div>
        <div className={stepClass(4)}>
          <span className="import-step-num">{stepNum(4)}</span> Confirmação
        </div>
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="form-card">
          <div className="form-card-body">
            <div className="hint-banner">
              Importe posições da <strong>Área do Investidor B3</strong> ou arquivos da corretora
              (XP, Clear, BTG). Mesmo ativo = <strong>atualiza só o valor</strong> + última
              atualização (upsert) e <strong>append</strong> no histórico de preço — nunca duplica
              nem apaga a carteira.
            </div>
            <div className="form-section-title">Escolha a fonte do arquivo</div>
            <div className="source-grid">
              {SOURCES.map(s => (
                <button
                  key={s.id}
                  type="button"
                  className={`source-option${source === s.id ? ' selected' : ''}`}
                  onClick={() => setSource(s.id)}
                >
                  <div className="radio" />
                  <div className="source-option-body">
                    <div className="source-option-title">{s.title}</div>
                    <div className="source-option-help">{s.help}</div>
                  </div>
                  <span className={`chip ${s.id.startsWith('b3') ? 'chip-green' : 'chip-blue'}`}>
                    {s.formats}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="form-card-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/investments')}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setFile(null);
                setParseError('');
                setStep(2);
              }}
            >
              Continuar
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="form-card">
          <div className="form-card-body">
            <div className="fonte-chip-row">
              <span className="muted">Fonte selecionada</span>
              <span className="chip chip-green">
                <span className="chip-dot" />
                {meta.title
                  .replace('Área do Investidor ', '')
                  .replace(' (arquivo da corretora)', '')}
              </span>
            </div>

            {parseError && (
              <div className="alert-banner red" style={{ marginBottom: 14 }}>
                <strong>Não foi possível ler o arquivo</strong>
                {parseError}
              </div>
            )}

            <div className="form-section-title">Envie o arquivo</div>
            <input
              ref={fileRef}
              type="file"
              accept={meta.accept}
              style={{ display: 'none' }}
              onChange={e => onPickFile(e.target.files?.[0] || null)}
            />

            <div
              className={`upload-zone${parseError ? ' error' : file ? ' ready' : ''}`}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                onPickFile(e.dataTransfer.files?.[0] || null);
              }}
            >
              <div className="upload-icon">{parseError ? '!' : file ? '✓' : '↑'}</div>
              <h3>
                {parseError
                  ? 'Arquivo inválido ou ilegível'
                  : file
                    ? 'Arquivo pronto para revisão'
                    : 'Arraste o arquivo ou escolha no computador'}
              </h3>
              <p>
                {parseError
                  ? `${file?.name || 'Arquivo'} · formato ou layout não reconhecido para esta fonte.`
                  : 'Substitua o arquivo se precisar — formatos aceitos dependem da fonte escolhida.'}
              </p>
              {file && !parseError && (
                <div className="file-pill">
                  <div className="file-pill-icon">📄</div>
                  <div>
                    <strong>{file.name}</strong>
                    <span>
                      {formatBytes(file.size)} · {meta.formats}
                    </span>
                  </div>
                </div>
              )}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => fileRef.current?.click()}
              >
                {file
                  ? parseError
                    ? 'Escolher outro arquivo'
                    : 'Trocar arquivo'
                  : 'Escolher arquivo'}
              </button>
              <div className="upload-hint">
                Formatos: {meta.formats} · até 10 MB · {meta.title.split('—')[0].trim()}
              </div>
            </div>
          </div>
          <div className="form-card-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>
              Voltar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!file || !!parseError || previewMutation.isLoading}
              style={!file || !!parseError ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
              onClick={() => {
                if (!file) return;
                setParseError('');
                previewMutation.mutate(file);
              }}
            >
              {previewMutation.isLoading ? 'Lendo arquivo…' : 'Continuar para revisão'}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && preview && (
        <div className="form-card">
          <div className="form-card-body">
            <div className="alert-banner amber" style={{ marginBottom: 14 }}>
              <strong>Upsert por mesmo investimento — só atualiza o valor.</strong>
              Ativos já na carteira terão <em>apenas</em> valor/preço e{' '}
              <strong>Última atualização</strong> alterados. Não duplica linhas. A carteira{' '}
              <strong>NÃO</strong> será apagada. Valores anteriores permanecem no{' '}
              <strong>histórico de preço</strong> (série para gráficos).
            </div>

            <div className="summary-box" style={{ marginBottom: 16 }}>
              <div className="summary-row">
                <span>Fonte</span>
                <strong>{preview.sourceLabel}</strong>
              </div>
              <div className="summary-row">
                <span>Arquivo</span>
                <strong>{preview.filename}</strong>
              </div>
              <div className="summary-row">
                <span>Data de referência</span>
                <strong>{preview.referenceDate}</strong>
              </div>
              <div className="summary-row">
                <span>Última atualização (será gravada)</span>
                <strong>{preview.ultimaAtualizacaoPreview}</strong>
              </div>
              <div className="summary-row">
                <span>Totais</span>
                <strong>
                  <span style={{ color: 'var(--green-700)' }}>{summary.novos} novos</span>
                  {' · '}
                  <span style={{ color: 'var(--amber-700)' }}>
                    {summary.atualizar} atualizar valor
                  </span>
                  {' · '}
                  {summary.ignorados} ignorado{summary.ignorados === 1 ? '' : 's'}
                </strong>
              </div>
              <div className="summary-row">
                <span>Valor nas linhas a importar</span>
                <strong>{formatCurrency(summary.valorTotal)}</strong>
              </div>
              <div className="summary-row">
                <span>Pontos de histórico</span>
                <strong>+{summary.historyPoints} (append · série de preço)</strong>
              </div>
            </div>

            <div className="form-section-title">Posições identificadas</div>
            <div className="card" style={{ boxShadow: 'none', margin: 0 }}>
              <div className="table-wrap review-table">
                <table className="data-table compact-mobile">
                  <thead>
                    <tr>
                      <th>Ativo</th>
                      <th className="hide-m">Tipo</th>
                      <th className="hide-m" style={{ textAlign: 'right' }}>
                        Qtd
                      </th>
                      <th className="hide-m" style={{ textAlign: 'right' }}>
                        Preço
                      </th>
                      <th className="hide-m" style={{ textAlign: 'right' }}>
                        Valor
                      </th>
                      <th>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => {
                      const effective = !r.include || r.action === 'ignorar' ? 'ignorar' : r.action;
                      return (
                        <tr key={r.rowKey}>
                          <td>
                            <div className="entity-cell">
                              <div className="entity-avatar">{(r.ticker || '?').slice(0, 3)}</div>
                              <div>
                                <strong>{r.ticker}</strong>
                                <br />
                                <span className="muted">{r.description}</span>
                                <div className="stack-extra hide-d">
                                  {categoryShort(r.category)} · {formatCurrency(r.valor)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="hide-m">{categoryShort(r.category)}</td>
                          <td className="hide-m" style={{ textAlign: 'right' }}>
                            {r.quantity != null
                              ? r.quantity.toLocaleString('pt-BR', { maximumFractionDigits: 6 })
                              : '—'}
                          </td>
                          <td className="amount hide-m" style={{ textAlign: 'right' }}>
                            {r.unitPrice != null ? formatCurrency(r.unitPrice) : '—'}
                          </td>
                          <td className="amount hide-m" style={{ textAlign: 'right' }}>
                            {formatCurrency(r.valor)}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="link-btn"
                              style={{
                                background: 'none',
                                border: 0,
                                padding: 0,
                                cursor: 'pointer',
                              }}
                              onClick={() => toggleIgnore(r.rowKey)}
                              title="Alternar Ignorar"
                            >
                              <StatusChip
                                label={actionLabel(effective)}
                                tone={actionTone(effective)}
                              />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="hint-banner" style={{ marginTop: 14, marginBottom: 0 }}>
              Cada linha <strong>Novo</strong> ou <strong>Atualizar valor</strong> gera um ponto no
              histórico de preço com timestamp. Clique no chip para <strong>Ignorar</strong>. Linhas
              ignoradas não alteram nada.
            </div>

            {summary.atualizar > 0 && (
              <label
                className={`confirm-check${confirmUpsert ? '' : ' unchecked'}`}
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={confirmUpsert}
                  onChange={e => setConfirmUpsert(e.target.checked)}
                  style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                />
                <div className="box">{confirmUpsert ? '✓' : ''}</div>
                <div>
                  Confirmo a atualização de valor dos ativos existentes
                  {updateNames ? ` (${updateNames})` : ''}. A carteira atual não será apagada nem
                  duplicada.
                </div>
              </label>
            )}

            {commitError && (
              <div className="alert-banner red" style={{ marginTop: 14 }}>
                <strong>Não foi possível confirmar</strong>
                {commitError}
              </div>
            )}
          </div>
          <div className="form-card-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setStep(2)}>
              Voltar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canConfirm || commitMutation.isLoading || summary.historyPoints === 0}
              style={
                !canConfirm || summary.historyPoints === 0
                  ? { opacity: 0.45, cursor: 'not-allowed' }
                  : undefined
              }
              onClick={() => {
                setCommitError('');
                commitMutation.mutate();
              }}
            >
              {commitMutation.isLoading ? 'Importando…' : 'Confirmar importação'}
            </button>
          </div>
        </div>
      )}

      {/* STEP 4 */}
      {step === 4 && commitResult && (
        <div className="form-card">
          <div className="form-card-body">
            <div className="alert-banner green" style={{ marginBottom: 16 }}>
              <strong>Importação concluída</strong>
              Posições atualizadas com sucesso.{' '}
              <strong>Última atualização: {commitResult.ultimaAtualizacaoLabel}</strong> gravada em
              cada posição afetada e no contexto da carteira.
            </div>
            <div className="success-hero">
              <div className="success-icon">✓</div>
              <h3>Carteira mesclada com sucesso</h3>
              <p>
                {commitResult.created} novos · {commitResult.updated} com valor atualizado (sem
                duplicar) · {commitResult.ignored} ignorado{commitResult.ignored === 1 ? '' : 's'}.
                Histórico de preço com append — nenhum ponto antigo foi sobrescrito.
              </p>
            </div>
            <div className="kpi-mini-row">
              <div className="kpi-mini">
                <div className="kpi-label">Novos</div>
                <div className="kpi-value green">{commitResult.created}</div>
              </div>
              <div className="kpi-mini">
                <div className="kpi-label">Valor atualizado</div>
                <div className="kpi-value amber">{commitResult.updated}</div>
              </div>
              <div className="kpi-mini">
                <div className="kpi-label">Ignorados</div>
                <div className="kpi-value">{commitResult.ignored}</div>
              </div>
              <div className="kpi-mini">
                <div className="kpi-label">Valor importado</div>
                <div className="kpi-value" style={{ fontSize: 15 }}>
                  {formatCurrency(commitResult.valorImportado)}
                </div>
              </div>
            </div>
            <div className="history-hint">
              <div className="sparkline" aria-hidden="true">
                <svg width="120" height="28" viewBox="0 0 120 28" fill="none">
                  <polyline
                    points="2,22 22,18 42,20 62,12 82,14 100,6 118,8"
                    stroke="#16a34a"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="118" cy="8" r="3" fill="#16a34a" />
                </svg>
              </div>
              <div>
                <strong>Histórico de preço atualizado</strong>
                <span>
                  Série para gráficos · +{commitResult.historyAppended} pontos · valores anteriores
                  preservados
                </span>
              </div>
            </div>
            <div className="summary-box" style={{ marginTop: 16 }}>
              <div className="summary-row">
                <span>Fonte</span>
                <strong>{commitResult.sourceLabel}</strong>
              </div>
              <div className="summary-row">
                <span>Arquivo</span>
                <strong>{commitResult.filename}</strong>
              </div>
              <div className="summary-row">
                <span>Última atualização</span>
                <strong>{commitResult.ultimaAtualizacaoLabel}</strong>
              </div>
              <div className="summary-row">
                <span>Pontos de histórico</span>
                <strong>+{commitResult.historyAppended} (append)</strong>
              </div>
            </div>
          </div>
          <div className="form-card-footer">
            <button type="button" className="btn btn-secondary" onClick={resetToSource}>
              Importar outro arquivo
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/investments')}
            >
              Ver carteira
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportInvestments;
